import type {
  ChangeEvent,
  KeyboardEvent,
  FocusEvent,
  MouseEvent,
} from 'react';
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';

type AutoCompleteProps<T> = {
  // The list of items to show in the dropdown
  items: T[];
  // Function to get the display text for an item
  getItemValue: (item: T) => string;
  // Called when the user changes the input value
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  // Called when the user selects an item (by click or keypress)
  onSelect: (item: T) => void;
  // Called when the input field receives focus
  onFocus?: () => void;
  // Placeholder text for the input field
  placeholder?: string;
  // Name attribute for the input field
  name?: string;
};

export function AutoComplete<T>(props: AutoCompleteProps<T>) {
  const [value, setValue] = useState('');
  const [index, setIndex] = useState(-1);
  const [visible, setVisible] = useState(false);
  const [id, setId] = useState<string>('');

  // Refs to help manage event handling and focus
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Toggle dropdown visibility.
  const toggleVisibility = useCallback(() => {
    setVisible((prev) => !prev);
  }, []);

  // Get display text for an item.
  const getValueFromItem = useCallback(
    (item: T) => {
      return props.getItemValue(item);
    },
    [props],
  );

  // Safely retrieve an item at the given index.
  const getItemFromList = useCallback(
    (idx: number) => {
      return idx >= 0 && idx < props.items.length ? props.items[idx] : undefined;
    },
    [props.items],
  );

  // Handle selection of an item.
  const handleSelect = useCallback(
    (item: T) => {
      if (visible) {
        toggleVisibility();
      }
      props.onSelect(item);

      const displayValue = getValueFromItem(item);
      setValue(displayValue);
      setId((item as any).id || '');
      setIndex(-1);
    },
    [visible, toggleVisibility, props, getValueFromItem],
  );

  // Handle input value change.
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!visible) {
        toggleVisibility();
      }
      setValue(e.target.value);
      setIndex(-1);
      setId('');
      props.onChange(e);
    },
    [visible, toggleVisibility, props],
  );

  // Keyboard navigation.
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (!props.items.length) return;
      const lastIndex = props.items.length - 1;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = index + 1;
        const newIndex = next > lastIndex ? 0 : next;
        setIndex(newIndex);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = index - 1;
        const newIndex = prev < 0 ? lastIndex : prev;
        setIndex(newIndex);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const selectedItem = getItemFromList(index);
        if (selectedItem) {
          handleSelect(selectedItem);
        }
      } else if (e.key === 'Escape') {
        setIndex(-1);
        setVisible(false);
      }
    },
    [props.items, index, handleSelect, getItemFromList],
  );

  // Hide the dropdown on blur.
  const handleBlur = useCallback(
    (e: FocusEvent<HTMLDivElement>) => {
      setTimeout(() => {
        if (
          containerRef.current &&
          !containerRef.current.contains(document.activeElement)
        ) {
          setVisible(false);
          setIndex(-1);
        }
      }, 150);
    },
    [],
  );

  // Show the dropdown on focus.
  const handleFocus = useCallback(() => {
    if (props.items.length && value.trim() !== '') {
      setVisible(true);
    }

    if (props.onFocus) {
      props.onFocus();
    }
  }, [props, value]);

  // Render each item in the dropdown.
  const renderItem = (item: T, idx: number) => {
    const displayText = getValueFromItem(item);
    const isSelected = idx === index;
    const handleMouseDown = (event: MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      handleSelect(item);
    };

    // Inline style for each item.
    const itemStyle: React.CSSProperties = {
      padding: '10px 15px',
      fontFamily: 'var(--font-bold)',
      fontSize: '14px',
      color: 'var(--gray-d)',
      cursor: 'pointer',
      backgroundColor: isSelected ? 'var(--blue-l)' : undefined,
    };

    return (
      <div
        key={(item as any).id || idx}
        role="option"
        aria-selected={isSelected}
        onMouseDown={handleMouseDown}
        style={itemStyle}
      >
        {displayText}
      </div>
    );
  };

  useEffect(() => {
    if (!value && visible) {
      setVisible(false);
    }
  }, [value, visible]);

  // Inline style for the container.
  const containerStyle: React.CSSProperties = {
    position: 'relative',
    height: '100%',
  };

  // Inline style for the input: no full border; only underline.
  const inputStyle: React.CSSProperties = {
    fontFamily: 'HarmoniaSans W01',
    border: 'none',
    borderBottom: '1px solid #d3d3d3',
    outline: 'none',
    width: '100%',
    backgroundColor: 'transparent',
    fontSize: '16px',
    lineHeight: '32px',
  };

  // Base style for the dropdown list.
  const listStyleBase: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: 'var(--pri-01)',
    borderRadius: '4px',
    boxShadow: '0px 1px 2px 1px rgba(238,238,238,1)',
    zIndex: 3,
    maxHeight: '300px',
    overflowY: 'auto',
  };

  // Combine list style with visibility settings.
  const dropdownStyle: React.CSSProperties =
    visible && props.items.length > 0
      ? { ...listStyleBase, display: 'block', marginBottom: '50px' }
      : { ...listStyleBase, display: 'none' };

  return (
    <div ref={containerRef} onBlur={handleBlur} onFocus={handleFocus} style={containerStyle}>
      <input
        ref={inputRef}
        type="text"
        name={props.name}
        value={value}
        placeholder={props.placeholder || ''}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        style={inputStyle}
      />
      <div style={dropdownStyle} role="listbox">
        {props.items.map((item, idx) => renderItem(item, idx))}
      </div>
    </div>
  );
}

export default AutoComplete;